import moment from 'moment'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useAppNotificationActions from '../functionalities/AppNotification/useAppNotificationActions'
import useArrayDiff from '../hooks/useArrayDiff'
import { UserContext, UserContextType } from '../UserContext'
import NewSessionInvitationDialogContents from './NewSessionInvitationDialogContents'
import useAnalytics from '../services/useAnalytics'
import { AnalyticsEvents } from '../services/AnalyticsService'
import { ExternalEventEmitter } from '../ExternalEventEmitter'
import CrossPlatformDialog from './components/CrossPlatformDialog'

export type NewSessionInvitationData = {
    uuid: string
    roomJoinCode: string
    roomName: string
    timestamp: Date
    username: string
}

export default function NewSessionInvitationDialogController() {
    const { state } = useContext(UserContext) as UserContextType

    const [dialogData, set_dialogData] = useState<NewSessionInvitationData | null>(null)

    const history = useHistory()
    const { logUserActivityWithContext } = useAnalytics()
    const { showAppNotification } = useAppNotificationActions()

    const appEventsDiff = useArrayDiff(state.appEvents, (a, b) => a.uuid === b.uuid)
    useEffect(() => {
        const ev = appEventsDiff.added.filter((ev) => ev.code === 'session_invitation' && !ev.isRead)[0] || null
        if (ev) {
            const timestamp = new Date(ev.timestamp)
            const { uuid, roomJoinCode, roomName, username } = ev

            const ttl = moment().diff(moment(timestamp), 'minutes')
            if (ttl > 5) {
                console.log(
                    `NewSessionInvitationDialogController: not showing because invite message ` +
                        `ttl=${ttl} > 5 minutes. timestamp="${timestamp}"`
                )
                return
            }

            console.log(
                `NewSessionInvitationDialogController: invite message ` +
                    `ttl=${ttl} > 5 minutes. timestamp="${timestamp}"`
            )

            // show native notification
            showAppNotification({
                title: 'Muse session invitation',
                body: `${username} invited you to join a session.`,
            })

            set_dialogData((d) => {
                if (d?.uuid === uuid) {
                    return d
                } else {
                    return {
                        uuid,
                        timestamp,
                        roomJoinCode,
                        roomName,
                        username,
                    }
                }
            })
        }
    }, [state.appEvents])

    const onClose = useCallback(() => {
        set_dialogData(null)
    }, [])

    const onAcceptInvitation = useCallback(() => {
        if (dialogData?.roomJoinCode) {
            // emit event 'do-exit-session'
            ExternalEventEmitter.set({
                eventName: 'join-room-link',
                value: `museapp://join-room?code=${dialogData.roomJoinCode}`,
            })
            logUserActivityWithContext(AnalyticsEvents.INVITE_ACCEPTED)
        }
        onClose()
    }, [history, dialogData])

    return (
        <>
            {dialogData && (
                <CrossPlatformDialog frameName="Dialog:newSessionInvitation" onDefaultClose={onClose}>
                    <NewSessionInvitationDialogContents
                        dialogData={dialogData}
                        onAcceptInvitation={onAcceptInvitation}
                        onDefaultClose={onClose}
                    />
                </CrossPlatformDialog>
            )}
        </>
    )
}
